import * as React from 'react';
import * as LogRocket from 'logrocket';
import injectSheet from 'react-jss';
import { Modal, Input, Button, message, Icon, Divider, Tooltip } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import TagsInput from 'src/components/common/TagsInput';
import { validateEmail } from 'src/utils/utils';
import {
  updateEmailListBL,
  updateBulkEmailListBL,
  shareStatus,
  updateBulkEmailListCN,
  updateEmailListCN,
} from 'src/api/shipmentTracking';
import { SHIPMENT_TYPE } from './ContainerUtils';
import { CardData } from './Interfaces/Shipment.interface';
import { onlyUnique, isValidArray } from 'src/Library/Helper';
import { getBulkRequestArray } from './ShipmentTrackingHelper';
import { tokens } from 'src/design-system';
import { StylesProps } from 'src/Library/Types';

const styles = theme => ({
  flexRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  iconStyle: {
    fontSize: tokens.fontSize.body,
    color: tokens.colors.text,
    cursor: 'pointer',
  },
  heading: {
    color: tokens.colors.textSecondary,
    marginRight: tokens.spacing.xl,
    fontWeight: tokens.fontWeight.semibold,
    marginBottom: tokens.spacing.sm,
  },
  reciepient: {
    color: tokens.colors.textSecondary,
    fontWeight: tokens.fontWeight.semibold,
    fontSize: tokens.fontSize.body,
    marginTop: tokens.spacing.xl,
  },
  subscriber: {
    color: tokens.colors.textSecondary,
    fontWeight: tokens.fontWeight.semibold,
    fontSize: tokens.fontSize.small,
  },
  emailTo: {
    color: tokens.colors.textSecondary,
    fontWeight: tokens.fontWeight.semibold,
    fontSize: tokens.fontSize.small,
    marginTop: tokens.spacing.md,
  },
  noEdit: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    marginTop: tokens.spacing.ms,
    fontSize: tokens.fontSize.small,
  },
  tooltip: {
    color: tokens.colors.text,
    marginLeft: tokens.spacing.sm,
    cursor: 'pointer',
  },
  edit: {
    border: `1px solid ${tokens.colors.border}`,
    width: '80%',
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.sm,
    marginTop: tokens.spacing.ms,
    background: tokens.colors.bgWhite,
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>> {
  items: CardData[];
  isSingleShare: boolean;
  isVisible: boolean;
  tabId: string;
  onModalClose: (success: boolean) => void;
}

interface IState {
  emails: string[];
  sharedWith: string[];
  sharingLink: string[];
  isSaving: boolean;
  isEditEnabled: boolean;
  isSharingLinkVisible: boolean;
}

const ShareTracking = (props: IProps) => {

  const [state, setStateUtil] = React.useState<IState>({
    isSaving: false,
    isSharingLinkVisible: false,
    sharingLink: [],
    isEditEnabled: false,
    sharedWith: [],
    emails: [],
  });

  const {
    items,
    isSingleShare,
    tabId,
    onModalClose,
    isVisible,
    classes,
  } = props;

  const {
    isSaving,
    sharingLink,
    isEditEnabled,
    sharedWith,
    emails,
  } = state;

  function setState(newState: Partial<IState>) {
    setStateUtil((oldSTate: IState) => {
      return {
        ...oldSTate,
        ...newState,
      };
    });
  }

  React.useEffect(() => {
    const sharingLink = tabId === SHIPMENT_TYPE.CONTAINER ? 'publicLink' : 'publicShareStatusLink';
    const filteredItems = items.filter(item => !!item[sharingLink]);
    const recipients = items[0].subscriberEmailList || [];
    if (isSingleShare) {
      setState({
        sharedWith: items[0].subscriberEmailList || [],
        sharingLink: filteredItems.map(item => item[sharingLink] || ''),
        emails: recipients.filter(onlyUnique),
      });
    } else {
      setState({
        sharingLink: filteredItems.map(item => item[sharingLink] || ''),
      });
    }
  }, []);

  function getBodyAndFuntion() {
    let body: any = {};
    let func;
    const itemIds = items.map(item => item.id).filter(onlyUnique);
    switch (tabId) {
      case SHIPMENT_TYPE.BL: {
        if (isSingleShare) {
          body.blId = itemIds[0];
          body.shipmentType = SHIPMENT_TYPE.BL;
        } else {
          body.blIds = itemIds;
        }
        func = isSingleShare ? updateEmailListBL : updateBulkEmailListBL;
        return { body, func };
      }
      case SHIPMENT_TYPE.CONTAINER: {
        if (isSingleShare) {
          body.containerId = itemIds[0];
          body.shipmentType = SHIPMENT_TYPE.CN;
        } else {
          body.containerIdList = itemIds;
        }
        func = isSingleShare ? updateEmailListCN : updateBulkEmailListCN;
        return { body, func };
      }
      default: {
        const shipments = getBulkRequestArray(items, tabId);
        body = { shipments };
        func = shareStatus;
        return { func, body };
      }
    }
  }

  async function handleOk() {
    if (!isValidArray(emails)) {
      message.error('No recipients added!!!');
      return;
    }
    const { body, func } = getBodyAndFuntion();
    const apiBody = {
      ...body,
      recipientEmailList: emails,
      subscriberEmailList: isSingleShare ? sharedWith : [],
    };
    setState({ isSaving: true });
    const response = await func(apiBody);
    setState({ isSaving: false });
    if (response.isSuccess) {
      message.success('Tracking shared successfully', 3);
      LogRocket.track('CT Tracking Link Shared');
      handleModalClose(true);
    } else {
      message.error(response.errorMessage);
    }
  }

  function handleModalClose(successClose = false) {
    onModalClose(successClose);
    setState({
      isSaving: false,
      isEditEnabled: false,
    });
  }

  function getAllEmails() {
    return sharedWith.join(', ');
  }

  function handleEmailChange(emails: string[]) {
    const validatedEmails: string[] = [];
    emails.forEach((email: string) => {
      if (validateEmail(email)) {
        validatedEmails.push(email);
      } else {
        message.error(`${email} is not a valid email. Please provide a valid email.`);
      }
    });
    setState({ emails: validatedEmails });
  }

  function handleDeleteClick(key: string) {
    setState({
      sharedWith: sharedWith.filter(e => e !== key),
    });
  }

  const renderListItem = (item, isLast: boolean) => {
    return <React.Fragment>
      <div className={classes.flexRow}>
        <span style={{
          color: tokens.colors.textTertiary,
          fontSize: tokens.fontSize.small,
        }}>
          {item}
        </span>
        <Icon
          className={classes.iconStyle}
          type="close"
          onClick={() => handleDeleteClick(item)}
        />
      </div>
      {!isLast ? <Divider style={{ margin: '8px 0' }} /> : null}
    </React.Fragment>;
  };

  function shouldDesable(): boolean {
    const emailList = items?.filter(onlyUnique)?.map(item => item.id);
    if (!isValidArray(emailList)) return true;
    return false;
  }

  const renderHeading = () => {
    return <span
      className={classes.heading}>
      Public Share Link
    </span>;
  };

  const renderEmailInput = () => {
    return <TagsInput
      style={{ width: '80%', marginTop: 6 }}
      autoFocus={true}
      value={state.emails}
      placeholder={'Enter user email'}
      notFoundContent={''}
      onChange={handleEmailChange}
      tokenSeparators={[' ']}
    />;
  };

  const renderLinks = () => {
    if (isSingleShare) {
      return <Input
        style={{ height: 36, width: '80%' }}
        value={sharingLink}
        readOnly
      />;
    }
    return <TagsInput
      style={{ width: '80%', marginTop: 6 }}
      autoFocus={true}
      value={sharingLink}
      notFoundContent={''}
      tokenSeparators={[' ']}
      onChange={() => {}}
      allowClear={false}
      removeIcon={''}
      readOnly
    />;
  };

  const renderCopy = () => {
    return <CopyToClipboard
      text={sharingLink.join(', ')}
      onCopy={() => message.success('Copied')}>
      <Button style={{
        height: 36,
        width: 100,
        background: '#F0F2F5',
        marginTop: isSingleShare ? undefined : '6px',
      }}>
        Copy
      </Button>
    </CopyToClipboard>;
  };

  const noEditBody = () => {
    if (isEditEnabled && !sharedWith?.length) return null;
    return <div className={classes.noEdit}>
      {`Shared with:  ${getAllEmails()}`}
      <Tooltip title="Edit">
        <Icon
          className={classes.tooltip}
          type="edit"
          onClick={() => setState({ isEditEnabled: true })}
        />
      </Tooltip>
    </div>;
  };

  const renderEdit = () => {
    if (!isEditEnabled) return null;
    return <div className={classes.edit}>
      <div className={classes.flexRow}>
        <span className={classes.subscriber}>
          Subscriber List
        </span>
        <a style={{ fontSize:'11px' }}
          onClick={() => setState({ sharedWith: [] })}>
          Delete All
        </a>
      </div>
      <div style={{ marginTop: 8 }}>
        {sharedWith.map((e, i) => renderListItem(e, i === sharedWith.length - 1))}
      </div>
    </div>;
  };

  const renderBody = () => {
    if (isSingleShare) return null;
    return <React.Fragment>
      {noEditBody()}
      {renderEdit()}
    </React.Fragment>;
  };

  return <Modal
    style={{ minWidth: 600 }}
    title="Share Tracking Link"
    visible={isVisible} width={600}
    okText="Submit"
    destroyOnClose
    onOk={() => handleOk()}
    onCancel={() => handleModalClose(false)}
    okButtonProps={{ disabled: shouldDesable() }}
    confirmLoading={isSaving}
  >
    {isValidArray(sharingLink) && renderHeading()}
      {isValidArray(sharingLink) && <div className={classes.flexRow}>
        {renderLinks()}
        {renderCopy()}
      </div>}
      <div className={classes.reciepient}> Add Recipients </div>
      <div className={classes.emailTo}> Email To </div>
      {renderEmailInput()}
      {renderBody()}
  </Modal>;
};

export default injectSheet(styles)(ShareTracking);
